<template>
  <b-card class="order-document-card">
    <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
      <div>
        <h4 class="mb-25">Order Document</h4>
      </div>

      <div v-if="activeFlags.length" class="document-badges d-flex flex-wrap mt-1 mt-md-0">
        <b-badge
            v-for="flag in activeFlags"
            :key="flag.label"
            :variant="flag.variant"
            pill
        >
          {{ flag.label }}
        </b-badge>
      </div>
    </div>

    <b-row>
      <b-col cols="12" xl="6" class="mb-2">
        <OrderDocumentCustomTable
            title="Order Information"
            :items="orderInformationRows"
        />
      </b-col>

      <b-col cols="12" xl="6" class="mb-2">
        <OrderDocumentCustomTable
            title="People & Assignment"
            :items="peopleRows"
        />
      </b-col>

      <b-col cols="12" xl="6" class="mb-2 mb-xl-0">
        <OrderDocumentCustomTable
            title="Percentages"
            :items="percentageRows"
        />
      </b-col>

      <b-col cols="12" xl="6">
        <OrderDocumentCustomTable
            title="Financial Breakdown"
            :items="financialRows"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { BBadge, BCard, BCol, BRow } from 'bootstrap-vue'
import OrderDocumentCustomTable from './OrderDocumentCustomTable.vue'

export default {
  name: 'OrderDocumentDetails',
  components: {
    OrderDocumentCustomTable,
    BBadge,
    BCard,
    BCol,
    BRow,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  computed: {
    activeFlags() {
      if (this.document.hasMiddleMan) {
        return [
          {
            label: 'Middleman Order',
            variant: 'warning',
          },
        ]
      }

      return [
        this.document.isPrivateOrder && {
          label: 'Private Order',
          variant: 'dark',
        },
        this.document.isCustomProduct && {
          label: 'Custom Product',
          variant: 'info',
        },
      ].filter(Boolean)
    },
    orderInformationRows() {
      return [
        this.row('Status', this.value(this.document.orderStatus), {
          badge: true,
          variant: 'light-primary',
        }),
        this.row('Type', this.value(this.document.orderType)),
        this.row('Currency', this.value(this.document.currencyName)),
        this.row('Created At', this.date(this.document.createdAt)),
        this.row('Expires At', this.date(this.document.expireAt, 'No expiration')),
      ]
    },
    peopleRows() {
      return [
        this.row('Customer', this.value(this.document.userName)),
        this.row('Admin', this.value(this.document.adminName)),
      ]
    },
    percentageRows() {
      const rows = [
        this.row('Admin', this.percentage(this.document.adminPercentage)),
        this.row('Booster', this.percentage(this.document.boosterPercentage)),
      ]

      if (this.document.hasMiddleMan) {
        rows.push(this.row('Middleman', this.percentage(this.document.middleManPercentage)))
      }

      rows.push(this.row('Platform Fee', this.percentage(this.document.platformFeePercentage)))
      return rows
    },
    financialRows() {
      const rows = [
        this.moneyRow('Total Price', this.document.totalPrice),
        this.moneyRow('Final Price', this.document.finalPrice),
        this.moneyRow('Admin Share', this.document.adminPrice),
        this.moneyRow('Booster Share', this.document.boosterPrice),
      ]

      if (this.document.hasMiddleMan) {
        rows.push(this.moneyRow('Middleman Commission', this.document.middleManCommissionAmount))
      }

      rows.push(
        this.moneyRow('Platform Fee', this.document.platformFeeAmount),
        this.moneyRow('Referrer Share', this.document.referrerPrice),
      )

      return rows
    },
  },
  methods: {
    row(label, value, options = {}) {
      return { label, value, ...options }
    },
    moneyRow(label, value) {
      return this.row(label, this.money(value), { money: true })
    },
    value(value, fallback = '—') {
      return value === null || value === undefined || value === '' ? fallback : value
    },
    percentage(value) {
      return value === null || value === undefined ? 'Not set' : `${value}%`
    },
    money(value) {
      if (value === null || value === undefined) return '—'

      const amount = Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value)

      return `${amount} ${this.document.currencyName || ''}`.trim()
    },
    date(value, fallback = '—') {
      if (!value) return fallback

      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-US')
    },
  },
}
</script>

<style lang="scss" scoped>
.order-document-card {
  border: 1px solid #ebe9f1;
}

.document-badges {
  gap: .5rem;
}
</style>
